import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ContentCut, Spa, CheckCircle, Error } from '@mui/icons-material';
import apiService from '../../services/api';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const token = searchParams.get('token');
        const email = searchParams.get('email');

        if (!token || !email) {
          setError('Invalid verification link. Please check your email and try again.');
          setVerifying(false);
          return;
        }

        console.log('Verifying email with token:', token, 'email:', email);

        // Call the verification API
        const response = await apiService.post('/auth/verify-email', {
          token: token,
          email: email
        });

        if (response.success) {
          setVerificationResult({
            success: true,
            message: response.message || 'Email verified successfully!'
          });
        } else {
          setError(response.message || 'Verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError(err.message || 'Verification failed. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToRegister = () => {
    navigate('/register');
  };

  if (verifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #dc2cb0 0%, #E91E63 50%, #8E24AA 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <Container maxWidth="md">
          <Card sx={{ boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Verifying your email...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we verify your email address.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #dc2cb0 0%, #E91E63 50%, #8E24AA 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {/* Logo and Brand */}
            <Box mb={4}>
              <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <ContentCut sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                <Spa sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Click<span style={{ color: '#dc2cb0' }}>Looks</span>
              </Typography>
            </Box>

            {verificationResult?.success ? (
              <>
                {/* Success Icon */}
                <Box mb={3}>
                  <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
                </Box>

                {/* Success Message */}
                <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  Email Verified! 🎉
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Your account has been successfully activated!
                </Typography>

                <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
                  {verificationResult.message}
                </Typography>

                {/* Success Info */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'success.light', borderRadius: 2 }}>
                  <Typography variant="body2" color="success.dark" sx={{ mb: 2 }}>
                    <strong>What's next?</strong>
                  </Typography>
                  <Typography variant="body2" color="success.dark" sx={{ mb: 1 }}>
                    • Your FREE trial is now active
                  </Typography>
                  <Typography variant="body2" color="success.dark" sx={{ mb: 1 }}>
                    • You can now login to your account
                  </Typography>
                  <Typography variant="body2" color="success.dark">
                    • Start managing your salon with ClickLooks
                  </Typography>
                </Box>

                {/* Action Button */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGoToLogin}
                  sx={{ 
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' }
                  }}
                >
                  Login to Your Account
                </Button>
              </>
            ) : (
              <>
                {/* Error Icon */}
                <Box mb={3}>
                  <Error sx={{ fontSize: 80, color: 'error.main' }} />
                </Box>

                {/* Error Message */}
                <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  Verification Failed
                </Typography>

                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  {error}
                </Alert>

                <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
                  There was a problem verifying your email address. This could be due to:
                </Typography>

                {/* Error Info */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'error.light', borderRadius: 2, textAlign: 'left' }}>
                  <Typography variant="body2" color="error.dark" sx={{ mb: 1 }}>
                    • The verification link has expired
                  </Typography>
                  <Typography variant="body2" color="error.dark" sx={{ mb: 1 }}>
                    • The link was already used
                  </Typography>
                  <Typography variant="body2" color="error.dark" sx={{ mb: 1 }}>
                    • Invalid verification token
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGoToRegister}
                    sx={{ 
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    Register Again
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleGoToLogin}
                    sx={{ 
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': { 
                        borderColor: 'primary.dark',
                        bgcolor: 'primary.main',
                        color: 'white'
                      }
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default EmailVerification;
